import React from 'react';
import Linkify from 'linkify-react';

import ExternalLinkAction from '@/components/primitive/external-link';
import { NON_COHORT_PREFIX } from '@/components/composed/slo-group/constants';

export function sortAlphabetically(a: string, b: string): number {
  if (a === '' || b === '') {
    throw new Error('`a` and `b` cannot be empty strings');
  }

  return a.localeCompare(b);
}

export function localNumberFormat(num: number | undefined): string | undefined {
  try {
    return num === undefined
      ? undefined
      : new Intl.NumberFormat(navigator.language || 'en-US').format(num);
  } catch (_) {
    // @see https://hello.jira.atlassian.cloud/browse/TOME-2589
    return undefined;
  }
}

/**
 * Convert link in string to <a> tag and returns react node array
 *
 * @param value
 * @return react node
 */
export const stringReplaceLink = (value: string): React.ReactNode => (
  <Linkify
    options={{
      render: {
        // eslint-disable-next-line react/display-name
        url: ({ content }) => <ExternalLinkAction text={content} href={content} />,
      },
    }}
  >
    {value}
  </Linkify>
);

export const validUrl = (url: string) => {
  try {
    // eslint-disable-next-line no-new
    new URL(url);
    return true;
  } catch (_) {
    return false;
  }
};

/**
 * Returns a substring between two strings. If the start or end string is not
 * found, the original string is returned.
 */
export const subStringBetween = (str: string, start: string, end: string): string => {
  const startIndex = str.indexOf(start);
  const endIndex = str.indexOf(end, startIndex + start.length);

  if (startIndex === -1 || endIndex === -1) {
    return str;
  }

  return str.substring(startIndex + start.length, endIndex);
};

/**
 * Converts a comma-separated string to cohortMembers query parameters format
 * Example: convertCommaSeparatedToQueryParams("prod,stg,dev", "environment")
 *   -> "environment:prod,environment:stg,environment:dev"
 */
export const convertCommaSeparatedToQueryParams = (
  values: string | undefined,
  paramName: string
): string =>
  values
    ? values
        .split(',')
        .map(value => {
          const trimmed = value.trim();
          return `${paramName}:${trimmed.startsWith(NON_COHORT_PREFIX) ? '' : trimmed}`;
        })
        .join(',')
    : '';
